/**
 * Thin HTTP client foundation for Phase J HQ API.
 * No domain modules — transport helpers only.
 */

export const API_VERSION_PREFIX = "/v1";

export interface ApiClientOptions {
  baseUrl: string;
  getAccessToken?: () => string | null | Promise<string | null>;
  getCorrelationId?: () => string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export function createApiClient(options: ApiClientOptions) {
  async function request<T>(
    path: string,
    init: RequestInit & { idempotencyKey?: string } = {},
  ): Promise<T> {
    const { idempotencyKey, headers: initHeaders, ...rest } = init;
    const headers = new Headers(initHeaders);
    headers.set("Accept", "application/json");
    if (!headers.has("Content-Type") && rest.body) {
      headers.set("Content-Type", "application/json");
    }
    headers.set("X-Huddl-Audience", "hq");

    const token = options.getAccessToken
      ? await options.getAccessToken()
      : null;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    if (options.getCorrelationId) {
      headers.set("X-Correlation-Id", options.getCorrelationId());
    }
    if (idempotencyKey) {
      headers.set("Idempotency-Key", idempotencyKey);
    }

    const url = `${options.baseUrl.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
    const response = await fetch(url, { ...rest, headers });
    const text = await response.text();
    const body = text ? (JSON.parse(text) as unknown) : null;

    if (!response.ok) {
      throw new ApiError(`HTTP ${response.status}`, response.status, body);
    }
    return body as T;
  }

  return {
    request,
    get: <T>(path: string) => request<T>(path, { method: "GET" }),
    post: <T>(path: string, body: unknown, idempotencyKey?: string) =>
      request<T>(path, {
        method: "POST",
        body: JSON.stringify(body),
        ...(idempotencyKey ? { idempotencyKey } : {}),
      }),
    patch: <T>(path: string, body: unknown, idempotencyKey?: string) =>
      request<T>(path, {
        method: "PATCH",
        body: JSON.stringify(body),
        ...(idempotencyKey ? { idempotencyKey } : {}),
      }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

export function apiErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const body = error.body as
      | { error?: { summary?: string; error_class?: string } }
      | null;
    if (body?.error?.summary) {
      return body.error.summary;
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return "Unexpected error";
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
